import {
  RichTextarea,
  RichTextareaHandle,
} from "@jugl-web/domain-resources/common/components/RichTextarea";
import { WhatsAppTemplateEditorToolbar } from "@jugl-web/domain-resources/integrations/components/WhatsAppTemplateEditorToolbar";
import { useTaskBoardList } from "@jugl-web/domain-resources/tasks/hooks/useTaskBoardList";
import { useTaskBoardsQuery } from "@jugl-web/domain-resources/tasks/hooks/useTaskBoardsQuery";
import { useTaskStatuses } from "@jugl-web/domain-resources/tasks/hooks/useTaskStatuses";
import { useRestApiProvider } from "@jugl-web/rest-api";
import {
  WhatsappTemplateCategory,
  WhatsappTemplatePayload,
  WhatsappTemplateValues,
} from "@jugl-web/rest-api/integrations/types";
import { TaskDefaultStatusId } from "@jugl-web/rest-api/tasks";
import { TaskBoardModel } from "@jugl-web/rest-api/tasks/models/TaskBoard";
import { TaskCustomStatusDto } from "@jugl-web/rest-api/tasks/models/TaskCustomStatus";
import {
  Alert,
  BoardAvatar,
  Button,
  DataLoadingWrapper,
  DotIcon,
  FormGroup,
  ListBoxItem,
  LoadingSpinner,
  TextInput,
} from "@jugl-web/ui-components";
import { SelectTrigger } from "@jugl-web/ui-components/cross-platform/forms/SelectTrigger";
import { ResourcePickerPopover } from "@jugl-web/ui-components/web/ResourcePickerPopover";
import { isWhatsappApiError, useToast, useTranslations } from "@jugl-web/utils";
import {
  UPDATES_AUTOMATION_DIALOG_SHOWN,
  getStorageItemWithFallback,
  saveItemToStorage,
} from "@jugl-web/utils/storage";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useMe } from "@web-src/features/app/hooks/useMe";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import { useSupportedWhatsappVariables } from "@jugl-web/domain-resources/integrations";
import { useScreenTransitionManager } from "@jugl-web/utils/utils/ScreenTransitionManager";
import { rawBodyToWhatsappBody } from "../../utils/rawBodyToWhatsappBody";
import { whatsappBodyToRawBody } from "../../utils/whatsappBodyToRawBody";
import alertAnimation from "./assets/alert-animation.json";
import { AutomationUpdatesDialog } from "./components/AutomationUpdatesDialog";
import { SectionHeader } from "./components/SectionHeader";
import { TemplatePreview } from "./components/TemplatePreview";
import { getWhatsappTemplateComponentsList } from "./utils/getWhatsappTemplateComponentsList";
import { ManageAccountsDrawerScreenToParametersMap } from "../../types";
import { WhatsappAccountTab } from "../WhatsappAccountScreen/types";

export const WhatsappTemplateFormScreen: React.FC<{
  templateCategory: WhatsappTemplateCategory;
  templateId?: string;
}> = ({ templateCategory, templateId }) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { transitionTo } =
    useScreenTransitionManager<ManageAccountsDrawerScreenToParametersMap>();
  const richTextareaRef = useRef<RichTextareaHandle | null>(null);

  const { entityId } = useEntitySelectedProvider();
  const { id: meId } = useMe();
  const { integrationsApi } = useRestApiProvider();
  const [createWhatsappTemplate, { isLoading }] =
    integrationsApi.useCreateWhatsappTemplateMutation();
  const [
    updateWhatsappTemplate,
    { isLoading: isUpdateWhatsappTemplateLoading },
  ] = integrationsApi.useUpdateWhatsappTemplateMutation();
  const [isAutomationUpdatesDialogOpen, setIsAutomationUpdatesDialogOpen] =
    useState(
      !getStorageItemWithFallback(UPDATES_AUTOMATION_DIALOG_SHOWN, false)
    );
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  const {
    data: whatsappTemplate,
    isLoading: isWhatsappTemplateLoading,
    isError: isWhatsappTemplateError,
    isFetching: isWhatsappTemplateFetching,
    refetch: refetchWhatsappTemplate,
  } = integrationsApi.useGetWhatsappTemplateQuery(
    templateId
      ? {
          entityId,
          templateId,
        }
      : skipToken
  );

  const handleCreateTemplate = async ({
    name,
    footer,
    board_id,
    action,
    header,
    bodyPlain,
  }: WhatsappTemplateValues) => {
    if (!richTextareaRef.current) {
      return;
    }

    const whatsappBody = rawBodyToWhatsappBody(bodyPlain, supportedVariables);

    const data: WhatsappTemplatePayload = {
      name,
      board_id,
      action,
      type: templateCategory as WhatsappTemplateCategory,
      template: {
        components: getWhatsappTemplateComponentsList({
          footer,
          header,
          body: whatsappBody.body,
          variables: whatsappBody.variables,
        }),
      },
    };

    const response = templateId
      ? await updateWhatsappTemplate({
          entityId,
          data,
          templateId,
        })
      : await createWhatsappTemplate({
          entityId,
          data,
        });

    if (response && "data" in response) {
      if (templateId) {
        toast(
          t({
            id: "client-support-page-page.whatsapp-template-was-updated-and-sent-to-approval",
            defaultMessage: "WhatsApp Update was updated and sent to Approval",
          })
        );
      } else {
        toast(
          t({
            id: "client-support-page-page.whatsapp-template-was-created-and-sent-to-approval",
            defaultMessage: "WhatsApp Update was created and sent to Approval",
          })
        );
      }

      if (templateCategory) {
        transitionTo({
          name: "whatsappAccount",
          tab:
            templateCategory === WhatsappTemplateCategory.message
              ? WhatsappAccountTab.messageTemplates
              : WhatsappAccountTab.taskTemplates,
        });
      }
    }
    if (response && "error" in response && isWhatsappApiError(response.error)) {
      if ("error" in response.error.data) {
        toast(response.error.data.error.error_user_msg, { variant: "error" });
        return;
      }
      const errorObj = response.error.data.errors?.[0];
      if (errorObj) {
        if (errorObj.field === "entity_id") {
          setIsAlertOpen(true);
          return;
        }
        toast(errorObj.message, { variant: "error" });
      }
    }
  };

  const {
    control,
    watch,
    handleSubmit,
    setValue,
    getValues,
    formState: { isDirty, errors },
  } = useForm<WhatsappTemplateValues>({
    mode: "all",
    defaultValues: {
      bodyPlain: "",
      bodyHtml: "",
      footer: "",
      header: "",
      name: "",
      board_id: undefined,
      action:
        templateCategory === WhatsappTemplateCategory.tasks
          ? TaskDefaultStatusId.pending
          : undefined,
    },
  });

  const bodyPlain = watch("bodyPlain");
  const bodyHtml = watch("bodyHtml");
  const header = watch("header");
  const footer = watch("footer");
  const boardId = watch("board_id");
  const statusId = watch("action");

  const { variables: supportedVariables } = useSupportedWhatsappVariables(
    entityId,
    boardId
  );

  const { getBoardByIdFromCache } = useTaskBoardsQuery({ entityId });

  const { accessibleBoards } = useTaskBoardList({
    entityId,
    meId: meId || "",
  });

  const boardsAsListItems = useMemo<ListBoxItem<TaskBoardModel>[]>(
    () => accessibleBoards.map((b) => ({ id: b.id, value: b })),
    [accessibleBoards]
  );

  const { allStatusesSorted, getStatusByIdWithFallback, getStatusColorById } =
    useTaskStatuses({
      entityId,
      boardId: boardId || null,
    });

  const statusesAsListItems = useMemo<ListBoxItem<TaskCustomStatusDto>[]>(
    () => allStatusesSorted.map((status) => ({ id: status.id, value: status })),
    [allStatusesSorted]
  );

  const board = boardId ? getBoardByIdFromCache(boardId) : undefined;
  const status = statusId ? getStatusByIdWithFallback(statusId) : undefined;

  const isDefaultValuesSet = useRef(false);
  useEffect(() => {
    if (whatsappTemplate && !isDefaultValuesSet.current) {
      isDefaultValuesSet.current = true;
      setValue("name", whatsappTemplate.name);
      setValue("board_id", whatsappTemplate.board_id);
      setValue("action", whatsappTemplate.action);
      setValue("header", whatsappTemplate.header);
      setValue("bodyPlain", whatsappTemplate.body);
      setValue("footer", whatsappTemplate.footer);
    }
  }, [whatsappTemplate, setValue]);
  useEffect(() => {
    if (whatsappTemplate) {
      const rawBody = whatsappBodyToRawBody(whatsappTemplate).replace(
        /{{[\s\S]*?}}| /g,
        (m) => (m === " " ? "&#32;" : m)
      );
      richTextareaRef.current?.readFromWhatsAppMarkdown(rawBody, {
        supportedVariables,
      });
    }
  }, [supportedVariables, getValues, whatsappTemplate]);

  return (
    <DataLoadingWrapper
      isLoading={isWhatsappTemplateLoading}
      isFetching={isWhatsappTemplateFetching}
      isError={isWhatsappTemplateError}
      onRetry={refetchWhatsappTemplate}
    >
      <div className="flex h-full flex-col overflow-hidden">
        <div className="jugl__custom-scrollbar overflow-auto py-[50px]">
          <div className="flex flex-col gap-[50px]">
            <div className="px-[60px]">
              <div className="w-[calc(100%-352px)] min-w-[320px] max-w-[1000px]">
                <SectionHeader
                  number={1}
                  title={
                    templateCategory === WhatsappTemplateCategory.tasks
                      ? t({
                          id: "client-support-page-page.name-and-action-trigger",
                          defaultMessage: "Name & Action Trigger",
                        })
                      : t({
                          id: "client-support-page-page.template-name",
                          defaultMessage: "Template Name",
                        })
                  }
                />
                {templateCategory === WhatsappTemplateCategory.message && (
                  <div className="text-dark-700 border-dark-400 font-secondary mt-8 border-y-0 border-l border-r-0 border-solid pl-[10px]">
                    {t(
                      {
                        id: "client-support-page-page.message-name-info-string",
                        defaultMessage: `Message Templates can be <boldText>manually sent</boldText> to the Client through <boldText>‘Customer Support’</boldText> Chats module. This action can be done any time, without any additional trigger`,
                      },
                      {
                        boldText: (text: (string | JSX.Element)[]) => (
                          <span className="text-bold text-primary-800">
                            {text}
                          </span>
                        ),
                      }
                    )}
                  </div>
                )}
                <Controller
                  control={control}
                  name="name"
                  rules={{
                    required: true,
                    maxLength: 60,
                  }}
                  disabled={!!templateId}
                  render={({ field: { onChange, ...props } }) => {
                    const handleChange = (
                      e: React.ChangeEvent<HTMLInputElement>
                    ) => {
                      const input = e.target;
                      const cursorPosition = input.selectionStart;
                      const newValue = e.target.value
                        .trimStart()
                        .toLowerCase()
                        .replace(/\s+/g, "_");

                      setValue("name", newValue, {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                      });

                      // Restore cursor position after the value update
                      requestAnimationFrame(() => {
                        if (input.setSelectionRange) {
                          const newCursorPosition = Math.min(
                            cursorPosition || 0,
                            newValue.length
                          );
                          input.setSelectionRange(
                            newCursorPosition,
                            newCursorPosition
                          );
                        }
                      });
                    };

                    return (
                      <div className="mt-8">
                        <TextInput
                          label={t({
                            id: "client-support-page-page.name",
                            defaultMessage: "Name",
                          })}
                          isRequired
                          lengthIndicator={{
                            max: 60,
                            current: props.name.length,
                          }}
                          isInvalid={!!errors.name}
                          autoComplete="off"
                          {...props}
                          onChange={handleChange}
                        />
                      </div>
                    );
                  }}
                />
                {templateCategory === WhatsappTemplateCategory.tasks && (
                  <div className="mt-8 flex flex-col">
                    <div className="flex items-center gap-8">
                      <FormGroup
                        isRequired
                        label={t({
                          id: "client-support-page-page.board",
                          defaultMessage: "Board",
                        })}
                        classNames={{ wrapperClassName: "w-1/2" }}
                      >
                        <Controller
                          name="board_id"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <ResourcePickerPopover
                              adjustToTriggerWidth
                              placement="bottom-end"
                              items={boardsAsListItems}
                              hasSearch
                              selectionBehavior={{
                                mode: "single",
                                canToggle: false,
                              }}
                              maxVisibleItems={6}
                              defaultSelectedIds={value ? [value] : undefined}
                              itemSize="sm"
                              spaceBetweenItems="compact"
                              renderTrigger={({
                                Trigger,
                                triggerRef,
                                isOpen,
                              }) => (
                                <Trigger
                                  ref={triggerRef}
                                  as={SelectTrigger}
                                  label={
                                    board ? (
                                      <div className="flex items-center gap-1.5">
                                        <BoardAvatar
                                          size="md"
                                          name={board.name}
                                          color={board.color}
                                        />
                                        {board.name}
                                      </div>
                                    ) : (
                                      t({
                                        id: "common.select",
                                        defaultMessage: "Select",
                                      })
                                    )
                                  }
                                  hasValue={!!board}
                                  isOpen={isOpen}
                                  className="h-[56px]"
                                />
                              )}
                              renderLabel={(item) => item.value.name}
                              renderStartIcon={(item) => (
                                <BoardAvatar
                                  size="md"
                                  name={item.value.name}
                                  color={item.value.color}
                                />
                              )}
                              onSelect={({ item, onClose }) => {
                                onChange(item.id);
                                setValue("action", TaskDefaultStatusId.pending);
                                richTextareaRef.current?.clear();
                                setValue("bodyPlain", "");
                                setValue("bodyHtml", "");
                                onClose();
                              }}
                            />
                          )}
                        />
                      </FormGroup>
                      <FormGroup
                        isRequired
                        label={t({
                          id: "client-support-page-page.when-status-set-to",
                          defaultMessage: "When Status set to",
                        })}
                        classNames={{ wrapperClassName: "w-1/2" }}
                      >
                        <Controller
                          name="action"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <ResourcePickerPopover
                              adjustToTriggerWidth
                              placement="bottom-end"
                              items={statusesAsListItems}
                              hasSearch
                              shouldFilterOnSearch
                              selectionBehavior={{
                                mode: "single",
                                canToggle: false,
                              }}
                              maxVisibleItems={6}
                              defaultSelectedIds={value ? [value] : undefined}
                              itemSize="sm"
                              spaceBetweenItems="compact"
                              renderTrigger={({
                                Trigger,
                                triggerRef,
                                isOpen,
                              }) => (
                                <Trigger
                                  ref={triggerRef}
                                  as={SelectTrigger}
                                  label={
                                    board
                                      ? status
                                        ? status.text
                                        : t({
                                            id: "common.select",
                                            defaultMessage: "Select",
                                          })
                                      : status
                                      ? status.text
                                      : t({
                                          id: "client-support-page-page.select-board-first",
                                          defaultMessage: "Select Board first",
                                        })
                                  }
                                  hasValue={!!status}
                                  isDisabled={!board}
                                  isOpen={isOpen}
                                  className="h-[56px]"
                                />
                              )}
                              renderLabel={(item) => item.value.text}
                              renderStartIcon={(item) => (
                                <DotIcon
                                  color={getStatusColorById(item.value.id)}
                                />
                              )}
                              onSelect={({ item, onClose }) => {
                                onChange(item.id);
                                onClose();
                              }}
                            />
                          )}
                        />
                      </FormGroup>
                    </div>
                    <div className="border-grey-400 text-dark-700 font-secondary mt-[18px] w-[600px] border-0 border-l border-solid pl-2.5 text-sm leading-[21px]">
                      {boardId
                        ? t(
                            {
                              id: "client-support-page-page.trigger-description",
                              defaultMessage:
                                "When the Order (Task) Status is set to “{statusName}“ – WhatsApp update will be sent to the Customer specified in the Task details.",
                            },
                            { statusName: status?.text }
                          )
                        : t(
                            {
                              id: "client-support-page-page.trigger-description-no-board",
                              defaultMessage:
                                "When a Status is assigned to “{statusName}“ – WhatsApp update will be sent to the Customer specified in the Task details. To select the Trigger Status, please choose the Board first.",
                            },
                            { statusName: status?.text }
                          )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="h-0.5 w-full shrink-0 bg-[#EEF2F5]" />
            <div className="px-[60px]">
              <SectionHeader
                number={2}
                title={t({
                  id: "client-support-page-page.message-content",
                  defaultMessage: "Message Content",
                })}
              />
              <div className="mt-8 flex w-full min-w-[320px] max-w-[1352px] items-start gap-8">
                <div className="flex grow flex-col gap-8">
                  <Controller
                    control={control}
                    name="header"
                    rules={{
                      maxLength: 60,
                    }}
                    render={({ field: { onChange, ...props } }) => (
                      <TextInput
                        label={t({
                          id: "client-support-page-page.header",
                          defaultMessage: "Header",
                        })}
                        lengthIndicator={{
                          max: 60,
                          current: header.length,
                        }}
                        isInvalid={!!errors.header}
                        onChange={(e) => onChange(e.target.value.trimStart())}
                        {...props}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="bodyPlain"
                    rules={{
                      required: true,
                      maxLength: 1024,
                    }}
                    render={({ field: { ref } }) => (
                      <FormGroup
                        label={t({
                          id: "client-support-page-page.body",
                          defaultMessage: "Body",
                        })}
                        isRequired
                        isInvalid={!!errors.bodyPlain}
                        errorMessage={errors.bodyPlain?.message}
                        lengthIndicator={{
                          max: 1024,
                          current: bodyPlain.length,
                        }}
                      >
                        <RichTextarea
                          ref={(e) => {
                            ref(e);
                            richTextareaRef.current = e;
                          }}
                          namespace="whatsapp-template-editor"
                          onEachUpdate={() => {
                            if (!richTextareaRef.current) {
                              return;
                            }

                            setValue(
                              "bodyPlain",
                              richTextareaRef.current.saveToWhatsAppMarkdown({
                                supportedVariables,
                              }),
                              { shouldDirty: true, shouldValidate: true }
                            );

                            setValue(
                              "bodyHtml",
                              richTextareaRef.current.saveToHTML()
                            );
                          }}
                          classes={{
                            container: "w-full h-[160px]",
                            contentEditable:
                              "h-full overflow-y-auto text-sm text-base font-secondary leading-4 px-5 py-4 border border-solid border-grey-400 rounded-[10px] focus:outline focus:outline-2 focus:outline-primary-200",
                          }}
                          endSlot={
                            <WhatsAppTemplateEditorToolbar
                              entityId={entityId}
                              boardId={boardId}
                              hideVariables={
                                templateCategory ===
                                WhatsappTemplateCategory.message
                              }
                            />
                          }
                        />
                      </FormGroup>
                    )}
                  />
                  <Controller
                    control={control}
                    name="footer"
                    rules={{
                      maxLength: 60,
                    }}
                    render={({ field: { onChange, ...props } }) => (
                      <TextInput
                        label={t({
                          id: "client-support-page-page.footer",
                          defaultMessage: "Footer",
                        })}
                        lengthIndicator={{
                          max: 60,
                          current: footer.length,
                        }}
                        isInvalid={!!errors.footer}
                        onChange={(e) => onChange(e.target.value.trimStart())}
                        {...props}
                      />
                    )}
                  />
                </div>
                <div>
                  <FormGroup
                    label={t({
                      id: "client-support-page-page.preview",
                      defaultMessage: "Preview",
                    })}
                  >
                    <TemplatePreview
                      bodyPlain={bodyPlain}
                      bodyHtml={bodyHtml}
                      footer={footer}
                      header={header}
                    />
                  </FormGroup>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2.5 border-0 border-t-2 border-solid border-[#EEF2F5] py-6 px-[70px]">
          <Button
            color="grey"
            className="h-10 w-[140px]"
            onClick={() => {
              transitionTo({
                name: "whatsappAccount",
                tab:
                  templateCategory === WhatsappTemplateCategory.message
                    ? WhatsappAccountTab.messageTemplates
                    : WhatsappAccountTab.taskTemplates,
              });
            }}
          >
            {t({
              id: "common.cancel",
              defaultMessage: "Cancel",
            })}
          </Button>
          <Button
            className="h-10 w-[200px]"
            onClick={handleSubmit(handleCreateTemplate)}
            isDisabled={
              isLoading ||
              isUpdateWhatsappTemplateLoading ||
              (!!templateId && !isDirty)
            }
          >
            {isLoading || isUpdateWhatsappTemplateLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              t({
                id: "common.save",
                defaultMessage: "Save",
              })
            )}
          </Button>
        </div>
      </div>
      <AutomationUpdatesDialog
        isOpen={isAutomationUpdatesDialogOpen}
        onRequestClose={() => {
          saveItemToStorage(UPDATES_AUTOMATION_DIALOG_SHOWN, true);
          setIsAutomationUpdatesDialogOpen(false);
        }}
      />
      <Alert
        isOpen={isAlertOpen}
        onRequestClose={() => setIsAlertOpen(false)}
        img={
          <Lottie
            options={{ animationData: alertAnimation }}
            width={70}
            height={70}
          />
        }
        title={t({
          id: "client-support-page-page.status-is-taken",
          defaultMessage: "Status is taken",
        })}
        content={t(
          {
            id: "client-support-page-page.template-status-already-assigned",
            defaultMessage:
              "Status {status} already assigned to WhatsApp Update. You can assign Status only to one WhatsApp Update at a time",
          },
          {
            status: statusId ? (
              <span className="text-primary-800 font-medium">
                {getStatusByIdWithFallback(statusId).text}
              </span>
            ) : null,
          }
        )}
        buttons={[
          {
            text: t({ id: "common.okay", defaultMessage: "Okay" }),
            onClick: () => setIsAlertOpen(false),
          },
        ]}
      />
    </DataLoadingWrapper>
  );
};
